// src/components/Admin/AdminTransactionsImport.js
"use client";

import { useEffect, useState } from "react";
import {
  Table,
  Button,
  Form,
  Spinner,
  Card,
  Row,
  Col,
  Alert,
} from "react-bootstrap";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import api from "../../services/api";
import { toast } from "react-toastify";
import MoneyInput from "../../components/Custombb/MoneyInput";

const TEMPLATE_COLUMNS = [
  "id",
  "code",
  "name",
  "family",
  "mobile",
  "codemelli",
  "wallet",
  "credit",
  "shop_all",
  "shop_all_old",
  "kasre_mahane",
  "description",
];

const TRANSACTION_FIELDS = [
  { key: "wallet", api: "wallet/charge", type: "CHARGE" },
  { key: "credit", api: "credit/add", type: "CHARGE" },
  { key: "shop_all", api: "transaction/add", type: "WITHDRAW", source: "shop_all" },
  { key: "shop_all_old", api: "transaction/add", type: "WITHDRAW", source: "shop_all_old" },
  { key: "kasre_mahane", api: "transaction/add", type: "WITHDRAW", source: "kasre_mahane" },
];

const AdminTransactionsImportComp = () => {
  const [rows, setRows] = useState([]); // uploaded rows + meta
  const [loading, setLoading] = useState(false);
  const [isLocked, setIsLocked] = useState(false); // قفل بعد از ثبت
  const [usersCache, setUsersCache] = useState([]); // from /admin/users/export
  const [globalError, setGlobalError] = useState("");

  const [batchId] = useState(
    () => `IMPORT-${Date.now()}`
  );

  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);

  useEffect(() => {
    fetchCategories(); // تابع جهت گرفتن دسته‌بندی‌ها
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get('/merchant-categories?isActive=true');
      setCategoryOptions(response.data);
    } catch (err) {
      console.error('Failed to fetch categories', err);
    }
  };

  useEffect(() => {
    // preload users list once for fast matching
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/admin/users/export");
        // Expect data to be array of users with fields id, code, name, family, mobile, codemelli
        setUsersCache(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error(err);
        toast.error("خطا در دریافت لیست کاربران از سرور");
      }
    };
    fetchUsers();
  }, []);

  // -----------------------
  // Download template
  // -----------------------
  const downloadTemplate = () => {
    const sample = [
      {
        id: "",
        code: "",
        name: "",
        family: "",
        mobile: "",
        codemelli: "",
        wallet: "",
        credit: "",
        shop_all: "",
        shop_all_old: "",
        kasre_mahane: "",
        description: "",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(sample, { header: TEMPLATE_COLUMNS });

    ws["!cols"] = [
        { wch: 5, z: "@" }, // A = id
        { wch: 10, z: "@" }, // B = code
        { wch: 10, z: "@" }, // C = name
        { wch: 10, z: "@" }, // D = family
        { wch: 15, z: "@" }, // E = mobile
        { wch: 15, z: "@" }, // F = codemelli
        { wch: 20 },         // G = wallet -> عددی می‌ماند
        { wch: 20 },         // H = credit -> عددی می‌ماند
        { wch: 20 },         // I = shop_all -> عددی می‌ماند
        { wch: 20 },         // I = shop_all_old -> عددی می‌ماند
        { wch: 20 },         // I = kasre_mahane -> عددی می‌ماند
        { wch: 30, z: "@" }, // J = description
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Users");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(blob, "users-template.xlsx");
  };

  // -----------------------
  // Parse uploaded file
  // -----------------------
  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setGlobalError("");
    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = evt.target.result;
        const workbook = XLSX.read(data, { type: "binary" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        const mapped = json.map((item, i) => ({
          index: i + 1,
          id: item.id?.toString?.() ?? "",
          code: item.code?.toString?.() ?? "",
          name: item.name ?? "",
          family: item.family ?? "",
          mobile: item.mobile?.toString?.() ?? "",
          codemelli: item.codemelli?.toString?.() ?? "",
          wallet: item.wallet ?? 0,
          credit: item.credit ?? 0,
          shop_all: item.shop_all ?? 0,
          shop_all_old: item.shop_all_old ?? 0,
          kasre_mahane: item.kasre_mahane ?? 0,
          description: item.description ?? "",

          // meta fields
          userId: null,
          matchedBy: "",
          found: false,
          duplicate: false,
          duplicateReason: "",
          status: "pending", // pending | sending | success | error
          error: "",
        }));

        // run validation and matching
        validateAndMatch(mapped, usersCache);
      } catch (err) {
        console.error(err);
        setGlobalError("خطا در خواندن فایل اکسل. فرمت فایل را بررسی کنید.");
      }
    };
    reader.readAsBinaryString(file);
  };

  // -----------------------
  // Matching priority: id -> code -> mobile -> codemelli
  // -----------------------
  const matchRowToUser = (row, users) => {
    if (!users || users.length === 0) return { userId: null, matchedBy: "" };

    // try id
    if (row.id) {
      const byId = users.find((u) => String(u.id) === String(row.id));
      if (byId) return { userId: byId.id, matchedBy: "id", name: byId.name, family: byId.family };
    }

    // try code
    if (row.code) {
      const byCode = users.find((u) => String(u.code) === String(row.code));
      if (byCode) return { userId: byCode.id, matchedBy: "code", name: byCode.name, family: byCode.family };
    }

    // try mobile
    if (row.mobile) {
      const byMobile = users.find((u) => String(u.mobile) === String(row.mobile));
      if (byMobile) return { userId: byMobile.id, matchedBy: "mobile", name: byMobile.name, family: byMobile.family };
    }

    // try codemelli
    if (row.codemelli) {
      const byCM = users.find((u) => String(u.codemelli) === String(row.codemelli));
      if (byCM) return { userId: byCM.id, matchedBy: "codemelli", name: byCM.name, family: byCM.family };
    }

    return { userId: null, matchedBy: "" };
  };

  // -----------------------
  // Validate and detect duplicates
  // -----------------------
  const detectDuplicates = (currentRows) => {
    // duplicates if same non-empty id OR code OR mobile OR codemelli among rows
    const mapId = new Map();
    const mapCode = new Map();
    const mapMobile = new Map();
    const mapCodemelli = new Map();
    const matchedUserMap = new Map(); // userId -> [indices]

    currentRows.forEach((r, idx) => {
      const i = idx;
      if (r.id) {
        const key = String(r.id);
        if (!mapId.has(key)) mapId.set(key, []);
        mapId.get(key).push(i);
      }
      if (r.code) {
        const key = String(r.code);
        if (!mapCode.has(key)) mapCode.set(key, []);
        mapCode.get(key).push(i);
      }
      if (r.mobile) {
        const key = String(r.mobile);
        if (!mapMobile.has(key)) mapMobile.set(key, []);
        mapMobile.get(key).push(i);
      }
      if (r.codemelli) {
        const key = String(r.codemelli);
        if (!mapCodemelli.has(key)) mapCodemelli.set(key, []);
        mapCodemelli.get(key).push(i);
      }
      if (r.userId) {
        const key = String(r.userId);
        if (!matchedUserMap.has(key)) matchedUserMap.set(key, []);
        matchedUserMap.get(key).push(i);
      }
    });

    // mark duplicates
    const mark = Array(currentRows.length).fill({ duplicate: false, reasons: [] });

    const applyMap = (m, typeName) => {
      for (const [k, arr] of m.entries()) {
        if (k === "" || k == null) continue;
        if (arr.length > 1) {
          const rowsList = arr.map((i) => currentRows[i].index).join(", ");
          arr.forEach((i) => {
            if (!mark[i].duplicate) mark[i] = { duplicate: true, reasons: [] };
            mark[i].reasons.push(`${typeName} تکراری در سطر(ها): ${rowsList}`);
          });
        }
      }
    };

    applyMap(mapId, "ID");
    applyMap(mapCode, "کد");
    applyMap(mapMobile, "موبایل");
    applyMap(mapCodemelli, "کدملی");

    // if same userId matched multiple rows, mark as duplicate
    for (const [userId, arr] of matchedUserMap.entries()) {
      if (userId === "null" || !userId) continue;
      if (arr.length > 1) {
        const rowsList = arr.map((i) => currentRows[i].index).join(", ");
        arr.forEach((i) => {
          if (!mark[i].duplicate) mark[i] = { duplicate: true, reasons: [] };
          mark[i].reasons.push(`کاربر یکسان در سطر(ها): ${rowsList}`);
        });
      }
    }

    // apply marks to rows
    const updated = currentRows.map((r, idx) => {
      const m = mark[idx];
      if (m && m.duplicate) {
        return {
          ...r,
          duplicate: true,
          duplicateReason: m.reasons.join(" | "),
          found: r.found, // keep found state
        };
      }
      return { ...r, duplicate: false, duplicateReason: "" };
    });

    return updated;
  };

  // -----------------------
  // validate and match full set
  // -----------------------
  const validateAndMatch = (importedRows, users) => {
    const usersList = users && users.length ? users : usersCache;
    const updated = importedRows.map((row) => {
      const { userId, matchedBy, name, family } = matchRowToUser(row, usersList);
      const found = Boolean(userId);
      return {
        ...row,
        userId: userId ?? null,
        matchedBy: matchedBy ?? "",
        found,
        name: found ? name : row.name,
        family: found ? family : row.family,
        status: "pending",
        error: found ? "" : row.error || "کاربر یافت نشد",
      };
    });

    const withDuplicates = detectDuplicates(updated);
    setRows(withDuplicates);
  };

  // -----------------------
  // update single cell (editable id/code/mobile/codemelli/wallet/credit/shop_all/shop_all_old/kasre_mahane/description)
  // -----------------------
  const updateRow = (index, key, value) => {
    if (isLocked) return;
    const updated = rows.map((r, i) => (i === index ? { ...r, [key]: value } : r));
    // re-match and re-validate
    validateAndMatch(updated, usersCache);
  };

  // -----------------------
  // Submit all (call APIs)
  // -----------------------
  const submitAll__old = async () => {
    setGlobalError("");
    setLoading(true);
    const updated = [...rows];

    for (let i = 0; i < updated.length; i++) {
      const r = updated[i];

      // skip invalid
      if (!r.found || r.duplicate) {
        updated[i] = {
          ...r,
          status: "error",
          error: r.error || r.duplicateReason || "ردیف آماده ارسال نیست",
        };
        setRows([...updated]);
        continue;
      }

      updated[i] = { ...r, status: "sending", error: "" };
      setRows([...updated]);

      try {
        for (const t of TRANSACTION_FIELDS) {
          const amount = Number(r[t.key]);
          if (!amount || amount <= 0) continue;

          await api.post(`/admin/users/${r.userId}/${t.api}`, {
            amount,
            type: t.type,
            source: t.source,
            description: r.description || undefined,
          });
        }

        updated[i] = { ...r, status: "success", error: "" };
      } catch (err) {
        console.error(err);
        updated[i] = {
          ...r,
          status: "error",
          error: err?.response?.data?.message || "خطا در ثبت",
        };
      }

      setRows([...updated]);
    }

    setLoading(false);
    setIsLocked(true); // قفل بعد از ثبت
    toast.success("عملیات ثبت تکمیل شد");
  };



  const submitBulk = async (targetRows) => {
    setGlobalError("");
    setLoading(true);

    const validRows = targetRows.filter(
      (r) => r.found && !r.duplicate
    );

    if (!validRows.length) {
      setLoading(false);
      toast.error("هیچ ردیف معتبری برای ارسال وجود ندارد");
      return;
    }

    const items = [];

    validRows.forEach((r) => {
      TRANSACTION_FIELDS.forEach((t) => {
        const amount = Number(r[t.key]);
        if (!amount || amount <= 0) return;

        items.push({
          rowIndex: r.index,
          userId: r.userId,
          amount,
          type: t.type,
          source: t.source ?? t.key,
          description: r.description || undefined,
          externalRef: `${batchId}|ROW-${r.index}|${t.source ?? t.key}`,
        });
      });

      console.log('batch_id', batchId);
    });

    if (!items.length) {
      setLoading(false);
      toast.error("هیچ تراکنشی برای ارسال ساخته نشد");
      return;
    }

    try {
      const { data } = await api.post("/admin/transactions/bulk", {
        items,
        ...(selectedCategoryId ? { categoryId: Number(selectedCategoryId) } : {}),
      });

      const updated = rows.map((r) => {
        const rowResults = data.results.filter(
          (x) => x.rowIndex === r.index
        );

        if (!rowResults.length) return r;

        const hasError = rowResults.some((x) => x.status === "error");

        return {
          ...r,
          status: hasError ? "error" : "success",
          error: hasError
            ? rowResults
                .filter((x) => x.status === "error")
                .map((x) => x.message)
                .join(" | ")
            : "",
        };
      });

      setRows(updated);

      toast.success(
        `ثبت انجام شد: ${data.success} موفق / ${data.error} خطا`
      );
    } catch (err) {
      console.error(err);
      setGlobalError(
        err?.response?.data?.message || "خطا در ارسال دسته‌جمعی"
      );
      toast.error("خطا در ارسال اطلاعات");
    } finally {
      setLoading(false);
    }
  };

  const submitAll = async () => {
    await submitBulk(rows);
    setIsLocked(true);
  };

  const retryErrors = async () => {
    const errorRows = rows.filter(
      (r) => r.status === "error"
    );

    if (!errorRows.length) {
      toast.info("ردیف خطاداری برای ارسال مجدد وجود ندارد");
      return;
    }

    // قبل از ارسال، وضعیت را reset کن
    const reset = rows.map((r) =>
      r.status === "error"
        ? { ...r, status: "pending", error: "" }
        : r
    );
    setRows(reset);

    await submitBulk(errorRows);
  };



  // -----------------------
  // Download results Excel
  // -----------------------
  const downloadResults = () => {
    if (!rows.length) return;
    const data = rows.map((r) => ({
      index: r.index,
      id: r.id,
      code: r.code,
      name: r.name,
      family: r.family,
      mobile: r.mobile,
      codemelli: r.codemelli,
      wallet: r.wallet,
      credit: r.credit,
      shop_all: r.shop_all,
      shop_all_old: r.shop_all_old,
      kasre_mahane: r.kasre_mahane,
      description: r.description,
      matchedBy: r.matchedBy,
      userId: r.userId ?? "",
      status: r.status,
      error: r.error || r.duplicateReason || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data, {
      header: [
        "index",
        "id",
        "code",
        "name",
        "family",
        "mobile",
        "codemelli",
        "wallet",
        "credit",
        "shop_all",
        "shop_all_old",
        "kasre_mahane",
        "description",
        "matchedBy",
        "userId",
        "status",
        "error",
      ],
    });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Results");
    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([wbout], { type: "application/octet-stream" }), "import-results.xlsx");
  };

  const deleteRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  return (
    <>
      <Card className="p-3 mb-3">
        <h5>ایمپورت تراکنش‌های Wallet / Credit</h5>

        <Form.Group className="mt-3">
          <Row className="align-items-center g-2">
            <Col sm={4}>
              <Button
                size="sm"
                variant="secondary"
                onClick={downloadTemplate}
                className="w-100"
                disabled={isLocked}
              >
                دانلود قالب Excel
              </Button>
            </Col>

            <Col sm={4} className="text-center d-flex align-items-center justify-content-center">
              <Form.Group className="mb-3">
                <Form.Label>انتخاب دسته محصول (اختیاری)<br />فقط برای کیف و اعتبار</Form.Label>
                <Form.Select
                  value={selectedCategoryId}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                >
                  <option value="">-- دسته ای انتخاب نشده --</option>
                  {categoryOptions.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.title}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col sm={4}>
              <Form.Label className="mb-0">انتخاب فایل Excel</Form.Label>
              <Form.Control
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFile}
                disabled={isLocked}
              />
            </Col>
          </Row>
        </Form.Group>

        {globalError && <Alert variant="danger" className="mt-2">{globalError}</Alert>}
      </Card>

      {rows.length > 0 && (
        <>
          <Table bordered hover responsive>
            <thead>
              <tr>
                <th>#</th>
                <th style={{ width: 75 }}>ID</th>
                <th style={{ width: 85 }}>کد</th>
                <th>نام</th>
                <th>خانوادگی</th>
                <th style={{ width: 150 }}>موبایل</th>
                <th style={{ width: 140 }}>کد ملی</th>
                <th style={{ width: 130 }}>Wallet</th>
                <th style={{ width: 130 }}>Credit</th>
                <th style={{ width: 130 }}>مانده بدهی</th>
                <th style={{ width: 130 }}>بدهی سال قبل</th>
                <th style={{ width: 130 }}>کسر ماهانه</th>
                <th>توضیحات</th>
                <th>Matched By</th>
                <th>وضعیت</th>
                <th>خطا</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {rows.map((r, i) => (
                <tr
                  key={i}
                  style={{
                    background: r.duplicate || !r.found ? "#fff0f0" : r.found ? "#f0fff0" : "transparent",
                  }}
                >
                  <td>{r.index}</td>

                  <td>
                    <Form.Control
                      value={r.id}
                      onChange={(e) => updateRow(i, "id", e.target.value)}
                      disabled={isLocked}
                      size="sm"
                    />
                  </td>

                  <td>
                    <Form.Control
                      value={r.code}
                      onChange={(e) => updateRow(i, "code", e.target.value)}
                      disabled={isLocked}
                      size="sm"
                    />
                  </td>

                  <td>{r.name || "-"}</td>
                  <td>{r.family || "-"}</td>

                  <td>
                    <Form.Control
                      value={r.mobile}
                      onChange={(e) => updateRow(i, "mobile", e.target.value)}
                      disabled={isLocked}
                      size="sm"
                    />
                  </td>

                  <td>
                    <Form.Control
                      value={r.codemelli}
                      onChange={(e) => updateRow(i, "codemelli", e.target.value)}
                      disabled={isLocked}
                      size="sm"
                    />
                  </td>

                  <td>
                    <MoneyInput
                        value={r.wallet}
                        onChange={(raw) => updateRow(i, "wallet", raw)}
                        disabled={isLocked}
                    />
                  </td>

                  <td>
                    <MoneyInput
                        value={r.credit}
                        onChange={(raw) => updateRow(i, "credit", raw)}
                        disabled={isLocked}
                    />
                  </td>

                  <td>
                    <MoneyInput
                      value={r.shop_all}
                      onChange={(raw) => updateRow(i, "shop_all", raw)}
                      disabled={isLocked}
                    />
                  </td>

                  <td>
                    <MoneyInput
                      value={r.shop_all_old}
                      onChange={(raw) => updateRow(i, "shop_all_old", raw)}
                      disabled={isLocked}
                    />
                  </td>

                  <td>
                    <MoneyInput
                      value={r.kasre_mahane}
                      onChange={(raw) => updateRow(i, "kasre_mahane", raw)}
                      disabled={isLocked}
                    />
                  </td>

                  <td>
                    <Form.Control
                      value={r.description}
                      onChange={(e) => updateRow(i, "description", e.target.value)}
                      disabled={isLocked}
                      size="sm"
                    />
                  </td>

                  <td>{r.found ? r.matchedBy : "-"}</td>

                  <td>
                    {r.status === "pending" && "در انتظار"}
                    {r.status === "sending" && <Spinner animation="border" size="sm" />}
                    {r.status === "success" && <span className="text-success">ثبت شد</span>}
                    {r.status === "error" && <span className="text-danger">خطا</span>}
                  </td>

                  <td className="text-danger">{r.error || r.duplicateReason}</td>

                  <td>
                    {!isLocked && (
                        <Button
                            variant="danger"
                            size="sm"
                            onClick={() => deleteRow(i)}
                            style={{ padding: "2px 6px", fontSize: "12px" }}
                        >
                            ✕
                        </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          <div className="d-flex gap-2 align-items-center">
            <Button
              onClick={submitAll}
              disabled={loading || isLocked}
              variant="primary"
            >
              {loading ? <Spinner size="sm" /> : "ثبت همه"}
            </Button>

            <Button
              variant="warning"
              onClick={retryErrors}
              disabled={loading || !rows.some(r => r.status === "error")}
            >
              ارسال مجدد خطادارها
            </Button>

            <Button
              variant="outline-secondary"
              onClick={downloadResults}
              disabled={!rows.length}
            >
              دانلود نتایج
            </Button>

            {isLocked && <span className="text-muted">لیست پس از ثبت قفل شد.</span>}
          </div>
        </>
      )}
    </>
  );
};

export default AdminTransactionsImportComp;
